import React, { Component, Fragment } from 'react';

import Modal, { ModalBody, ModalFooter, ModalHeader } from '@lightspeed/cirrus-modal';
import { Button } from '@lightspeed/flame/Button';
import { Progress } from '@lightspeed/flame/Progress';
import PropTypes from 'prop-types';

import { PRINTER_KINDS } from 'root/apps/pages/views/devices/types';
import { PrintEvents } from 'root/tracking.types';

import LabelPrintModal from '../LabelPrintModal';
import LabelPrintTemplate from '../LabelPrintTemplate';

/**
 * Class Printing displays the printing status
 */
class Printing extends Component {
  /**
   * Construct the Printing Modal
   * @param {Object} props - Component properties
   */
  constructor(props) {
    super(props);

    this.state = {
      currentLabel: 1,
      percentage: 5,
    };

    this.labelQuantityInterval = null;

    this.close = this.close.bind(this);
  }

  /**
   * React's Mounted component listener
   */
  componentDidMount() {
    this.print();
  }

  /**
   * Implement additional functionality to the onHide/close parent function
   */
  close() {
    this.stopProgressBarAnimation();
    this.props.onHide();
  }

  /**
   * Compile the template based on the user settings and
   * send the print command to the selected printer
   */
  print() {
    const { printer, labelData, labelQuantity, labelSize, labelMargin, labelContent } = this.props;
    const zplTemplate = this.generateZplTemplate({
      labelData,
      labelQuantity,
      labelSize,
      labelMargins: { [labelSize]: { left: labelMargin } },
      labelContent,
    });

    if (!printer || typeof printer.send !== 'function' || typeof zplTemplate !== 'string') {
      this.props.setStep(LabelPrintModal.steps.ERROR, { error: 'GENERIC' });
      return;
    }

    const printSpeedPerLabel = 500;
    const printStartDelay = 300;
    this.labelQuantityInterval = this.startProgressBarAnimation({
      printSpeedPerLabel,
      labelQuantity,
    });

    printer.send(zplTemplate, () => {
      window.dataLayer.push({
        event: PrintEvents.ZBP,
        model: printer.identification.model,
        kind: PRINTER_KINDS.LABEL,
        size: labelSize.toUpperCase(),
      });

      setTimeout(() => {
        this.close();
      }, printSpeedPerLabel * labelQuantity + printStartDelay);
    });
  }

  /**
   * Generate the zpl template(s) for the print request
   * @param {Object|Array} labelData
   * @param {Number} labelQuantity
   * @param {String} labelSize
   * @param {Number} labelMargins
   * @returns {string}
   */
  generateZplTemplate({ labelData, labelQuantity, labelSize, labelMargins, labelContent }) {
    let zplTemplate = '';
    let setHeaderCommands = true;

    try {
      if (!Array.isArray(labelData)) {
        // eslint-disable-next-line no-param-reassign
        labelData.copies = labelQuantity;
        // eslint-disable-next-line no-param-reassign
        labelData = [labelData];
      }

      // eslint-disable-next-line no-plusplus
      for (let i = 0, ii = labelData.length; i < ii; ++i) {
        zplTemplate += new LabelPrintTemplate({
          setHeaderCommands,
          labelSize,
          labelData: labelData[i],
          labelQuantity: labelData[i].copies,
          labelMargins,
          labelContent,
        }).zplTemplate;

        setHeaderCommands = false;
      }
    } catch (e) {
      zplTemplate = null;
    }

    return zplTemplate;
  }

  startProgressBarAnimation({ printSpeedPerLabel, labelQuantity }) {
    let currentLabel = 1;
    return setInterval(() => {
      if (currentLabel > labelQuantity) {
        return;
      }

      if (this.labelQuantityInterval) {
        this.setState({
          currentLabel,
          percentage: (100 / labelQuantity) * currentLabel,
        });
      }

      // eslint-disable-next-line no-plusplus
      currentLabel++;
    }, printSpeedPerLabel);
  }

  stopProgressBarAnimation() {
    clearInterval(this.labelQuantityInterval);
    this.labelQuantityInterval = null;
  }

  /**
   * Render the Printing modal
   * @returns {Object} DOM Element
   */
  render() {
    const { labelSize, labelData, totalProducts, labelQuantity, isQueue } = this.props;
    const { currentLabel, percentage } = this.state;

    return (
      <Modal
        className={`label-print-modal ${this.props.currentStep.toLowerCase()} cirrus responsive`}
        isOpen={this.props.show}
        onRequestClose={this.close}
      >
        <ModalHeader onCloseClicked={this.close}>
          Printing Label{' '}
          {labelData && (
            <Fragment>
              {isQueue ? ' Queue' : ''} -{' '}
              {isQueue ? (
                <Fragment>
                  <var>{totalProducts}</var> Items,
                  <var>{labelQuantity}</var> Labels
                </Fragment>
              ) : (
                <var>{labelData.Item.description}</var>
              )}
            </Fragment>
          )}
        </ModalHeader>
        <ModalBody>
          <div className="cr-pv-6 cr-text-center">
            <h4 className="cr-text-xl cr-mb-0">Printing Label</h4>
            <div className="cr-pt-4 cr-pb-6">
              <img
                className={`label-size-${labelSize}`}
                src={`https://d2o5po5b88zacb.cloudfront.net/dist/assets/lshash-118c0e2/images/label/label-size-${labelSize}.svg`}
                alt={`${labelSize} labels`}
              />
            </div>
            <div className="progress-container">
              <div className="progress-status">
                <span className="cr-text-s">
                  Printing <var>{currentLabel}</var> of <var>{labelQuantity || '1'}</var>
                </span>
              </div>
              <Progress
                type="determinate"
                className="notranslate"
                current={percentage}
                total={100}
              />
            </div>
          </div>
        </ModalBody>
        <ModalFooter className="cr-text-right">
          <Button onClick={this.close}>Hide this window</Button>
        </ModalFooter>
      </Modal>
    );
  }
}

Printing.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  printer: PropTypes.object.isRequired,
  labelSize: PropTypes.string.isRequired,
  labelData: PropTypes.oneOfType([PropTypes.object, PropTypes.array]).isRequired,
  totalProducts: PropTypes.number.isRequired,
  labelQuantity: PropTypes.number.isRequired,
  isQueue: PropTypes.bool.isRequired,
  setStep: PropTypes.func.isRequired,
  currentStep: PropTypes.string.isRequired,
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
};

export default Printing;
